/**
 * Search keyword → category alias map.
 *
 * Users naturally type category names like "bridal shower" rather than
 * the slug-style "bridal". When the typed keyword exactly matches one
 * of the phrases below (case-insensitive, trimmed), the search swaps
 * the keyword for a `pub_categoryLevel1` category filter and drops the
 * keyword. That broadens the result to every listing in the matching
 * category — closer to what the user expects when typing a category
 * name rather than searching kit titles for those literal words.
 *
 * Keys: lowercased phrase the user might type.
 * Values: category slug (must match a key in EditListingThemeSelectionForm
 * and EditListingDetailsForm category lists).
 *
 * Maintenance:
 *   - Add new entries when product introduces a category or notices
 *     search terms users try that don't match (check Mixpanel /
 *     server logs for empty-result keywords).
 *   - Slugs are flat in this project (categoryLevel1 only). If the
 *     category tree gets nested, this map needs slug paths.
 *   - Keep entries lowercase. The lookup function lowercases the input.
 *   - Single-word entries like "bridal" or "wedding" intentionally
 *     short-circuit keyword search to a category browse. If a future
 *     product decision wants those words to still match listing
 *     title/description text, remove the single-word entries here.
 */
const CATEGORY_ALIASES = {
  // Birthday
  'birthday': 'birthday',
  'birthday party': 'birthday',
  'first birthday': 'birthday',
  'kids birthday': 'birthday',
  'kids party': 'birthday',
  'sweet sixteen': 'birthday',
  'sweet 16': 'birthday',
  'quinceanera': 'birthday',

  // Bridal & Wedding
  'bridal': 'bridal',
  'bridal shower': 'bridal',
  'wedding': 'bridal',
  'wedding shower': 'bridal',
  'bachelorette': 'bridal',
  'bachelorette party': 'bridal',
  'engagement': 'bridal',
  'engagement party': 'bridal',
  'rehearsal dinner': 'bridal',

  // Baby Shower
  'baby shower': 'baby_shower',
  'gender reveal': 'baby_shower',
  'sip and see': 'baby_shower',
  'sprinkle': 'baby_shower',
  'baby sprinkle': 'baby_shower',
  'diaper party': 'baby_shower',

  // Milestone
  'milestone': 'milestone',
  'graduation': 'milestone',
  'graduation party': 'milestone',
  'anniversary': 'milestone',
  'retirement': 'milestone',
  'retirement party': 'milestone',
  'promotion party': 'milestone',
  'housewarming': 'milestone',

  // Themed Parties
  'themed party': 'themed_parties',
  'themed parties': 'themed_parties',
  'dinner party': 'themed_parties',
  'cocktail party': 'themed_parties',
  'halloween': 'themed_parties',
  'halloween party': 'themed_parties',
  'movie night': 'themed_parties',
  'game night': 'themed_parties',
  'taco tuesday': 'themed_parties',

  // Beach Day
  'beach': 'beach_day',
  'beach day': 'beach_day',
  'beach party': 'beach_day',
  'pool party': 'beach_day',
  'outdoor party': 'beach_day',
  'picnic': 'beach_day',
  'bbq': 'beach_day',
  'barbecue': 'beach_day',

  // Holiday
  'holiday': 'holiday',
  'christmas': 'holiday',
  'thanksgiving': 'holiday',
  'easter': 'holiday',
  'fourth of july': 'holiday',
  '4th of july': 'holiday',
  'new years': 'holiday',
  'new years eve': 'holiday',
  'valentines': 'holiday',
  'valentines day': 'holiday',
  'mothers day': 'holiday',
  'fathers day': 'holiday',

  // Cultural
  'cultural': 'cultural',
  'diwali': 'cultural',
  'lunar new year': 'cultural',
  'ramadan': 'cultural',
  'eid': 'cultural',
  'passover': 'cultural',
  'hanukkah': 'cultural',
  'kwanzaa': 'cultural',
  'cinco de mayo': 'cultural',
  'holi': 'cultural',
};

/**
 * Look up a category slug for a typed keyword.
 *
 * Match rules: lowercase + trim the input, then exact match against
 * the alias map keys. No fuzzy / partial matching — keeps behavior
 * predictable. Returns null if no alias matches.
 *
 * @param {string} keyword raw user input
 * @returns {string|null} category slug (e.g. 'bridal') or null
 */
export const getCategoryAlias = keyword => {
  if (typeof keyword !== 'string') return null;
  const normalized = keyword.trim().toLowerCase();
  if (!normalized) return null;
  return CATEGORY_ALIASES[normalized] || null;
};

export { CATEGORY_ALIASES };
